import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Building2, Copy, Check, Clock, 
  Crown, CheckCircle2, Loader2, Gift, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Banka bilgileri
const BANK_INFO = {
  bankName: "İş Bankası",
  accountName: "Datex Bilişim Anonim Şirketi",
  iban: "TR90 0006 4000 0012 0350 0047 98"
};

// Plan fiyatları - LANSMAN FİYATLARI
const PLAN_PRICES = {
  pro: {
    monthly: 590,
    quarterly: 1490,
    yearly: 4990
  }
};

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceCode, setReferenceCode] = useState('');
  const [loadingReference, setLoadingReference] = useState(true);
  
  // Referans kodu (indirim için)
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  
  // URL'den plan ve billing cycle al
  const queryParams = new URLSearchParams(location.search);
  const plan = queryParams.get('plan') || 'pro';
  const billingCycle = queryParams.get('billing') || 'monthly';
  const refFromUrl = queryParams.get('ref');
  
  const originalPrice = PLAN_PRICES[plan]?.[billingCycle] || 590;
  const discountAmount = Math.round(originalPrice * (discountPercent / 100));
  const finalPrice = originalPrice - discountAmount;
  const cycleName = billingCycle === 'yearly' ? 'Yıllık' : billingCycle === 'quarterly' ? '3 Aylık' : 'Aylık';

  // URL'den referans kodu varsa otomatik doğrula
  useEffect(() => {
    if (refFromUrl) {
      setCouponCode(refFromUrl);
      validateCoupon(refFromUrl);
    }
  }, [refFromUrl]);

  // Referans kodu al
  useEffect(() => {
    fetchReferenceCode();
  }, [plan, billingCycle]);

  const validateCoupon = async (code) => {
    if (!code || code.length < 4) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.get(`/referral/validate/${code}`);
      if (response.data.valid) {
        setCouponApplied(response.data);
        setDiscountPercent(response.data.discount_percent || 15);
        toast.success(response.data.message);
      } else {
        setCouponApplied(null);
        setDiscountPercent(0);
        toast.error(response.data.message || 'Geçersiz kod');
      }
    } catch (error) {
      setCouponApplied(null);
      setDiscountPercent(0);
      toast.error('Kod doğrulanamadı');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    setDiscountPercent(0);
  };

  const fetchReferenceCode = async () => {
    try {
      const response = await axios.get(
        `/membership/generate-reference?plan_type=${plan}&billing_cycle=${billingCycle}`
      );
      setReferenceCode(response.data.reference_code);
    } catch (error) {
      toast.error('Referans kodu oluşturulamadı');
      console.error(error);
    } finally {
      setLoadingReference(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success('Kopyalandı!');
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmitPayment = async () => {
    if (!referenceCode) {
      toast.error('Referans kodu bulunamadı');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/membership/payment-request', {
        plan_type: plan,
        billing_cycle: billingCycle,
        amount: finalPrice,
        transaction_id: referenceCode,
        note: couponApplied ? `İndirim kodu: ${couponCode} (%${discountPercent})` : '',
        discount_code: couponApplied ? couponCode : null,
        discount_percent: discountPercent,
        original_amount: originalPrice
      });
      
      setSubmitted(true);
      toast.success('Ödeme talebiniz alındı!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Başarılı gönderim ekranı
  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-dark py-12">
        <div className="container mx-auto px-6 max-w-lg">
          <Card className="border-green-500/50 bg-green-500/5">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle className="text-2xl text-green-400">Ödeme Talebiniz Alındı!</CardTitle>
              <CardDescription className="text-base">
                Referans kodunuz: <span className="font-mono text-white font-bold">{referenceCode}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {couponApplied && (
                <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg">
                  <p className="text-sm text-primary flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    %{discountPercent} indirim uygulandı! Ödemeniz gereken tutar: <span className="font-bold">₺{finalPrice}</span>
                  </p>
                </div>
              )}
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <h4 className="font-semibold mb-2">Sonraki Adımlar:</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Banka hesabımızda açıklama kontrol edilecek</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Onaylandığında e-posta ile bilgilendirileceksiniz</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Pro özellikleriniz otomatik aktif olacak</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded-lg text-sm">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-primary">Ödemeler genellikle 5-30 dakika içinde onaylanır.</span>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                Dashboard&apos;a Dön
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <div className="container mx-auto px-6 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/50">
            <Crown className="h-3 w-3 mr-1" />
            Pro Üyelik
          </Badge>
          <h1 className="text-3xl font-bold mb-2">Pro Üyelik Ödemesi</h1>
          <p className="text-slate-400">
            {cycleName} Plan – {discountPercent > 0 ? (
              <>
                <span className="line-through text-slate-500">₺{originalPrice}</span>{' '}
                <span className="text-primary font-semibold">₺{finalPrice}</span>
              </>
            ) : (
              <span className="text-primary font-semibold">₺{originalPrice}</span>
            )}
          </p>
        </div>

        <div className="grid gap-6">
          
          {/* İndirim Kodu Bölümü */}
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Gift className="h-5 w-5 text-primary" />
                İndirim Kodu
              </CardTitle>
              <CardDescription>
                Arkadaşınızdan aldığınız referans kodunu girin
              </CardDescription>
            </CardHeader>
            <CardContent>
              {couponApplied ? (
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-full">
                      <Sparkles className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-400">%{discountPercent} İndirim Uygulandı!</p>
                      <p className="text-sm text-slate-400">
                        {couponApplied.referrer_name} sizi davet etti
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={removeCoupon}
                    className="text-slate-400 hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Referans kodu (ör: AHMT1234)"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="font-mono uppercase"
                    maxLength={10}
                  />
                  <Button 
                    onClick={() => validateCoupon(couponCode)}
                    disabled={couponLoading || couponCode.length < 4}
                    className="px-6"
                  >
                    {couponLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Uygula'
                    )}
                  </Button>
                </div>
              )}
              
              {discountPercent > 0 && (
                <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Orijinal Fiyat</span>
                    <span className="line-through text-slate-500">₺{originalPrice}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-green-400">İndirim (%{discountPercent})</span>
                    <span className="text-green-400">-₺{discountAmount}</span>
                  </div>
                  <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-slate-700">
                    <span>Ödemeniz Gereken</span>
                    <span className="text-primary">₺{finalPrice}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Banka Bilgileri */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Banka Havale Bilgileri
              </CardTitle>
              <CardDescription>
                Aşağıdaki hesaba havale/EFT yapın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Banka */}
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Banka</p>
                  <p className="font-medium">{BANK_INFO.bankName}</p>
                </div>
              </div>

              {/* Hesap Adı */}
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Hesap Adı</p>
                  <p className="font-medium">{BANK_INFO.accountName}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => copyToClipboard(BANK_INFO.accountName, 'account')}
                >
                  {copied === 'account' ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* IBAN */}
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">IBAN</p>
                  <p className="font-mono font-medium">{BANK_INFO.iban}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => copyToClipboard(BANK_INFO.iban.replace(/\s/g, ''), 'iban')}
                >
                  {copied === 'iban' ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Tutar */}
              <div className="flex justify-between items-center p-3 bg-primary/10 border border-primary/30 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Tutar</p>
                  <p className="text-xl font-bold text-primary">₺{finalPrice}</p>
                  {discountPercent > 0 && (
                    <p className="text-xs text-green-400">%{discountPercent} indirimli fiyat</p>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => copyToClipboard(finalPrice.toString(), 'amount')}
                >
                  {copied === 'amount' ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Referans Kodu - ÖNEMLİ */}
              <div className="p-4 bg-amber-500/10 border-2 border-amber-500/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-amber-400 font-medium mb-1">⚠️ AÇIKLAMA (ÖNEMLİ)</p>
                    {loadingReference ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-slate-400">Referans kodu oluşturuluyor...</span>
                      </div>
                    ) : (
                      <p className="text-2xl font-mono font-bold text-white">{referenceCode}</p>
                    )}
                  </div>
                  {referenceCode && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      onClick={() => copyToClipboard(referenceCode, 'reference')}
                    >
                      {copied === 'reference' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" /> Kopyalandı
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" /> Kopyala
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-amber-400/80 mt-3">
                  Banka açıklamasına yalnızca bu kodu yazınız. Başka bir şey yazmayınız.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Ödeme Bildirimi */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Ödeme Bildirimi
              </CardTitle>
              <CardDescription>
                Havale/EFT yaptıktan sonra aşağıdaki butona tıklayın
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-slate-800/50 rounded-lg mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Referans Kodu</span>
                  <span className="font-mono font-bold text-white">{referenceCode || '...'}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-slate-400">Tutar</span>
                  <span className="font-bold text-primary">₺{finalPrice}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-slate-400">Plan</span>
                  <span>Pro {cycleName}</span>
                </div>
                {couponApplied && (
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-slate-400">İndirim</span>
                    <span className="text-green-400">%{discountPercent} ({couponCode})</span>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Button 
                className="w-full bg-primary hover:bg-primary/90"
                onClick={handleSubmitPayment}
                disabled={loading || !referenceCode}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Ödemeyi Yaptım
                  </>
                )}
              </Button>
              <Button 
                variant="ghost" 
                className="w-full text-slate-500 hover:text-slate-300"
                onClick={() => navigate('/pricing')}
              >
                Plan Değiştir
              </Button>
            </CardFooter>
          </Card>

          {/* Güven Mesajı */}
          <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <Clock className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-300">
                <span className="font-medium">Ödemeler genellikle 5–30 dakika içinde onaylanır.</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Yoğunluk durumunda en geç 1 iş günü içinde aktif edilir.
              </p>
            </div>
          </div>

          {/* Hukuki Not */}
          <p className="text-[11px] text-slate-600 text-center">
            Ödeme işlemleri, kullanıcı sözleşmesi ve hizmet şartları kapsamında değerlendirilir.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
